import DOMPurify from "dompurify";
import { useEffect, useRef, useState } from "react";

// Client functions
import { updateReadTranscript } from "sj-ui-toolkit/src/api";
import { getLessonTranscriptUrl } from "sj-ui-toolkit/src/client";

const CODE_STYLES_CSS = `pre code { color: #1E2126; }`;

const TranscriptShadow = ({ bodyHtml, styleCss }: { bodyHtml: string; styleCss: string }) => {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
    shadow.innerHTML = "";

    // CSS injected as text content — no sanitization needed
    const styleEl = document.createElement("style");
    styleEl.textContent = CODE_STYLES_CSS + styleCss;
    shadow.appendChild(styleEl);

    // HTML body sanitized with DOMPurify’s default config
    const body = document.createElement("div");
    body.innerHTML = DOMPurify.sanitize(bodyHtml);
    shadow.appendChild(body);
  }, [bodyHtml, styleCss]);

  return (
    <div
      ref={hostRef}
      style={{
        containerType: "inline-size",
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        wordBreak: "break-word",
      }}
    />
  );
};

interface TextViewerProps {
  lessonId: number;
  readTranscript: boolean;
}

const TextViewer = ({ lessonId, readTranscript }: TextViewerProps) => {
  const [transcriptBody, setTranscriptBody] = useState<string | null>(null);
  const [transcriptCss, setTranscriptCss] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTranscript = async () => {
      setLoading(true);
      try {
        const url = await getLessonTranscriptUrl({ lessonId: String(lessonId) });
        const response = await fetch(url);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const styleCss = Array.from(doc.querySelectorAll("style"))
          .map(el =>
            (el.textContent ?? "").replace(
              /@media\s*(\([^)]*(?:max-width|min-width)[^)]*\))/g,
              "@container $1"
            )
          )
          .join("\n");

        setTranscriptCss(styleCss);
        setTranscriptBody(doc.body.innerHTML);
      } catch (error) {
        console.error("Error fetching transcript:", error);
      }
      setLoading(false);
    };

    if (lessonId && transcriptBody === null) {
      fetchTranscript();
    }
  }, [lessonId]);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (!readTranscript && event.data === "reachedEnd") {
        // updateReadTranscript types levelId as required + lessonId as string; callers only have
        // the numeric lessonId and the backend tolerates the missing levelId. TODO(T12): loosen.
        const result = await updateReadTranscript({ lessonId: String(lessonId) } as {
          levelId: string;
          lessonId: string;
        });
        if (!result) {
          console.error("Failed to send read transcript event");
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [lessonId]);

  if (loading) return <div>Loading transcript...</div>;

  return <TranscriptShadow bodyHtml={transcriptBody ?? ""} styleCss={transcriptCss} />;
};

export default TextViewer;
